import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePaymentsReport, type UsePaymentsReportFilters } from '../../hooks/usePaymentsReport';
import type {
  PaymentMethod,
  PaymentStatus,
  PaymentsReportItem,
  PaymentsReportSummary,
} from '../../types/reports';
import authService from '../../services/authService';

const PRINT_STYLES = `
  @page {
    size: A4 portrait;
    margin: 18mm 16mm 24mm 16mm;
  }

  body {
    background: #ffffff !important;
    color: #0f172a !important;
    font-family: 'Poppins', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif !important;
  }

  .print-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 24px;
    animation: fadeIn 0.4s ease-in;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .print-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
    padding-bottom: 16px;
    border-bottom: 2px solid #e2e8f0;
  }

  .print-summary {
    display: grid;
    gap: 16px;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  }

  .print-summary-card {
    background: linear-gradient(145deg, #f8fbff, #eef2ff);
    border-radius: 16px;
    padding: 16px 20px;
    border: 1px solid rgba(59, 130, 246, 0.18);
    box-shadow: 0 10px 18px rgba(37, 99, 235, 0.08);
  }

  .print-summary-card .label {
    font-size: 11px;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.08em;
    color: #1d4ed8;
    margin-bottom: 6px;
  }

  .print-summary-card .value {
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
  }

  .print-section-title {
    font-size: 16px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #0f172a;
    margin: 12px 0;
  }

  .print-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
  }

  .print-table thead th {
    background: #0f172a;
    color: #ffffff;
    padding: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .print-table tbody td {
    border: 1px solid #d9e2ec;
    padding: 10px 12px;
  }

  .print-table tbody tr:nth-of-type(odd) {
    background: #f8fafc;
  }

  @media print {
    .print-hide {
      display: none !important;
    }
  }
`;

const PAYMENT_STATUS_LABEL: Record<PaymentStatus, string> = {
  PENDING: 'Pendente',
  APPROVED: 'Aprovado',
  REJECTED: 'Rejeitado',
  CANCELLED: 'Cancelado',
  EXPIRED: 'Expirado',
};

const PAYMENT_STATUS_VALUES: PaymentStatus[] = ['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED', 'EXPIRED'];

const PAYMENT_METHOD_LABEL: Record<Exclude<PaymentMethod, null>, string> = {
  PIX: 'PIX',
  CREDIT_CARD: 'Cartão de crédito',
  DEBIT_CARD: 'Cartão de débito',
  OTHER: 'Outros',
};

const PAYMENT_METHOD_VALUES: Exclude<PaymentMethod, null>[] = ['PIX', 'CREDIT_CARD', 'DEBIT_CARD', 'OTHER'];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);

const formatDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatDateDisplay = (value?: string) => {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

interface ParsedFilters {
  startDate?: string;
  endDate?: string;
  status: PaymentStatus[];
  paymentMethod: Exclude<PaymentMethod, null>[];
  pedidoId?: string;
  providerPaymentId?: string;
  consumerId?: string;
  minAmount?: string;
  maxAmount?: string;
  cnpj?: string;
}

const parseFiltersFromSearch = (search: string): ParsedFilters => {
  const params = new URLSearchParams(search);
  const startDate = params.get('startDate') || undefined;
  const endDate = params.get('endDate') || undefined;
  const statusParams = params.getAll('status') as PaymentStatus[];
  const paymentMethodParams = params.getAll('paymentMethod') as Exclude<PaymentMethod, null>[];
  const pedidoId = params.get('pedidoId') || undefined;
  const providerPaymentId = params.get('providerPaymentId') || undefined;
  const consumerId = params.get('consumerId') || undefined;
  const minAmount = params.get('minAmount') || undefined;
  const maxAmount = params.get('maxAmount') || undefined;
  const cnpj = params.get('cnpj') || undefined;

  const validStatus = statusParams.filter((status): status is PaymentStatus => PAYMENT_STATUS_VALUES.includes(status));
  const validMethods = paymentMethodParams.filter((method): method is Exclude<PaymentMethod, null> =>
    PAYMENT_METHOD_VALUES.includes(method),
  );

  return {
    startDate,
    endDate,
    status: validStatus.length > 0 ? validStatus : ['APPROVED'],
    paymentMethod: validMethods,
    pedidoId,
    providerPaymentId,
    consumerId,
    minAmount,
    maxAmount,
    cnpj,
  };
};

const buildQueryFilters = (filters: ParsedFilters, fallbackCnpj?: string): UsePaymentsReportFilters => {
  const query: UsePaymentsReportFilters = {};

  if (filters.startDate) {
    query.startDate = new Date(`${filters.startDate}T00:00:00`).toISOString();
  }

  if (filters.endDate) {
    query.endDate = new Date(`${filters.endDate}T23:59:59`).toISOString();
  }

  if (filters.status.length > 0) {
    query.status = filters.status;
  }

  if (filters.paymentMethod.length > 0) {
    query.paymentMethod = filters.paymentMethod;
  }

  if (filters.pedidoId) {
    query.pedidoId = filters.pedidoId;
  }

  if (filters.providerPaymentId) {
    query.providerPaymentId = filters.providerPaymentId;
  }

  if (filters.consumerId) {
    query.consumerId = filters.consumerId;
  }

  if (filters.minAmount) {
    const parsed = Number(filters.minAmount);
    if (!Number.isNaN(parsed)) {
      query.minAmount = parsed;
    }
  }

  if (filters.maxAmount) {
    const parsed = Number(filters.maxAmount);
    if (!Number.isNaN(parsed)) {
      query.maxAmount = parsed;
    }
  }

  const effectiveCnpj = filters.cnpj || fallbackCnpj;
  if (effectiveCnpj) {
    query.cnpj = effectiveCnpj.replace(/\D/g, '');
  }

  query.limit = 100;

  return query;
};

const PaymentsReportPrintPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useMemo(() => authService.getUser(), []);

  const parsedFilters = useMemo(() => parseFiltersFromSearch(location.search), [location.search]);

  const queryFilters = useMemo(
    () => buildQueryFilters(parsedFilters, user?.cnpj),
    [parsedFilters, user?.cnpj],
  );

  const { data, isLoading, isError } = usePaymentsReport(queryFilters, true);

  const summary: PaymentsReportSummary | undefined = data?.pages?.[0]?.summary;
  const items = useMemo<PaymentsReportItem[]>(() => data?.pages?.flatMap((page) => page.items ?? []) ?? [], [data]);

  const statusEntries = useMemo(
    () => (summary?.byStatus ? Object.entries(summary.byStatus) : []) as Array<[PaymentStatus, number]>,
    [summary],
  );

  const methodEntries = useMemo(
    () => (summary?.byMethod ? Object.entries(summary.byMethod) : []) as Array<[Exclude<PaymentMethod, null>, number]>,
    [summary],
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f4f6fb',
        py: 4,
        px: { xs: 2, md: 6 },
      }}
    >
      <style>{PRINT_STYLES}</style>

      <Box
        className="print-hide"
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        flexWrap="wrap"
        gap={2}
        mb={3}
      >
        <Box>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Visualização para impressão do relatório de pagamentos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Revise as informações e clique em "Imprimir" para gerar o PDF. Você pode voltar e ajustar filtros quando quiser.
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
            Voltar
          </Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()} disabled={isLoading}>
            Imprimir
          </Button>
        </Box>
      </Box>

      {isError && (
        <Alert severity="error" className="print-hide" sx={{ mb: 3 }}>
          Não foi possível carregar os dados do relatório. Volte e ajuste os filtros antes de tentar novamente.
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress size={48} />
        </Box>
      ) : (
        <Box
          sx={{
            bgcolor: '#ffffff',
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.12)',
          }}
        >
          <div className="print-wrapper">
            <div className="print-header">
              <Box>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  Relatório de pagamentos
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Emitido em {formatDateTime(new Date().toISOString())}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Período: {formatDateDisplay(parsedFilters.startDate)} – {formatDateDisplay(parsedFilters.endDate)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Status considerados: {statusEntries.length === 0 ? 'Todos' : statusEntries.map(([status]) => PAYMENT_STATUS_LABEL[status]).join(', ')}
                </Typography>
                {parsedFilters.paymentMethod.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Métodos: {parsedFilters.paymentMethod.map((method) => PAYMENT_METHOD_LABEL[method]).join(', ')}
                  </Typography>
                )}
                {parsedFilters.pedidoId && (
                  <Typography variant="body2" color="text.secondary">
                    Pedido: {parsedFilters.pedidoId}
                  </Typography>
                )}
              </Box>
              <Box textAlign="right">
                {user?.name && (
                  <Typography variant="body2" color="text.secondary">
                    Responsável: {user.name}
                  </Typography>
                )}
                {(parsedFilters.cnpj || user?.cnpj) && (
                  <Typography variant="body2" color="text.secondary">
                    CNPJ: {(parsedFilters.cnpj || user?.cnpj) ?? ''}
                  </Typography>
                )}
              </Box>
            </div>

            <div className="print-summary">
              <div className="print-summary-card">
                <div className="label">Pagamentos</div>
                <div className="value">{summary?.totalPayments ?? 0}</div>
              </div>
              <div className="print-summary-card">
                <div className="label">Valor total</div>
                <div className="value">{formatCurrency(summary?.totalAmount ?? 0)}</div>
              </div>
              <div className="print-summary-card">
                <div className="label">Valor aprovado</div>
                <div className="value">{formatCurrency(summary?.approvedAmount ?? 0)}</div>
              </div>
              <div className="print-summary-card">
                <div className="label">Ticket médio</div>
                <div className="value">{formatCurrency(summary?.averageTicket ?? 0)}</div>
              </div>
            </div>

            <Divider />

            <div>
              <div className="print-section-title">Distribuição por status</div>
              {statusEntries.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhum agrupamento por status disponível para o período selecionado.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Quantidade</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statusEntries.map(([status, count]) => (
                      <TableRow key={status}>
                        <TableCell>{PAYMENT_STATUS_LABEL[status]}</TableCell>
                        <TableCell align="right">{count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            <Divider />

            <div>
              <div className="print-section-title">Distribuição por método de pagamento</div>
              {methodEntries.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhum agrupamento por método disponível para o período selecionado.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Método</TableCell>
                      <TableCell align="right">Quantidade</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {methodEntries.map(([method, count]) => (
                      <TableRow key={method}>
                        <TableCell>{PAYMENT_METHOD_LABEL[method]}</TableCell>
                        <TableCell align="right">{count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            <Divider />

            <div>
              <div className="print-section-title">Pagamentos detalhados</div>
              {items.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhum pagamento encontrado com os filtros selecionados.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Data de criação</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Método</TableCell>
                      <TableCell align="right">Valor</TableCell>
                      <TableCell>Pedido</TableCell>
                      <TableCell>Telefone</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>ID provedor</TableCell>
                      <TableCell>Atualizado em</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                        <TableCell>{PAYMENT_STATUS_LABEL[item.status]}</TableCell>
                        <TableCell>
                          {item.paymentMethod
                            ? PAYMENT_METHOD_LABEL[item.paymentMethod as Exclude<PaymentMethod, null>] ?? item.paymentMethod
                            : '—'}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                        <TableCell>{item.pedidoId}</TableCell>
                        <TableCell>{item.customerPhone ?? '—'}</TableCell>
                        <TableCell>{item.customerEmail ?? '—'}</TableCell>
                        <TableCell>{item.providerPaymentId ?? '—'}</TableCell>
                        <TableCell>{formatDateTime(item.updatedAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </Box>
      )}
    </Box>
  );
};

export default PaymentsReportPrintPage;
